import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, updateDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

/**
 * Registers a new user with email and password, then saves user data in Firestore.
 * @param {Object} params
 * @param {string} params.firstName
 * @param {string} params.lastName
 * @param {string} params.email
 * @param {string} params.password
 * @param {string} params.avatar
 * @param {string} params.county
 * @param {string} params.city
 * @param {string} params.phone
 * @param {string} params.postCode
 * @param {string} params.streetAddress
 * @param {string} params.subscriptionName
 * @param {string} params.subscriptionType
 * @param {string} params.subscriptionStatus
 * @param {string} params.subscriptionStartAt
 * @param {string} params.subscriptionUpdateAt
 * @param {string} params.subscriptionEndedAt
 * @param {boolean} params.isTrial
 * @param {Date} params.trialStartDate
 * @param {Date} params.trialEndDate
 * @returns {Promise<{uid: string}>}
 */
export async function registerUser({
  firstName = '',
  lastName = '',
  email = '',
  password = '',
  avatar = '',
  county = '',
  city = '',
  phone = '',
  postCode = '',
  streetAddress = '',
  subscriptionName = '', // pro/standard/ultimate
  subscriptionType = '', // monthly/yearly
  subscriptionStatus = 'unpaid',
  subscriptionStartAt = null,
  subscriptionUpdateAt = null,
  subscriptionEndedAt = null,
  isTrial = false,
  trialStartDate = null,
  trialEndDate = null,
}) {
  // 1. Create user with email and password
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const { uid } = userCredential.user;

  // 2. Prepare user data for Firestore (only allowed fields)
  const userData = {
    uid,
    email,
    avatar,
    county,
    city,
    firstName,
    lastName,
    phone,
    postCode,
    streetAddress,
    termsAccepted: false,
    totalAssessments: 0,
    usedAssessments: 0,
    assessments: [],
    subscriptionName,
    subscriptionStatus,
    subscriptionType,
    subscriptionStartAt,
    subscriptionUpdateAt,
    subscriptionEndedAt,
    isTrial,
    trialStartDate,
    trialEndDate,
  };

  // 3. Save user data in Firestore with UID as document ID
  await setDoc(doc(db, 'Users', uid), userData, { merge: false });

  return { uid };
}

/**
 * Logs in a user with email and password using Firebase Auth.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>} The user object
 * @throws {Error} If login fails
 */
export async function loginUser(email, password) {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } 
  catch (error) {
    throw error;
  }
}

/**
 * Accepts terms and conditions for a user by setting termsAccepted: true in Firestore.
 * @param {string} uid - The user's UID
 * @returns {Promise<void>}
 */
export async function acceptTermsAndConditions(uid) {
  const userRef = doc(db, 'Users', uid);
  await updateDoc(userRef, { termsAccepted: true });
}

/**
 * Creates or updates a user document in Firestore with a custom UID.
 * @param {string} uid - The custom document ID (user UID)
 * @param {object} userData - The data to save
 * @returns {Promise<void>}
 */
export async function createOrUpdateUserWithUid(uid, userData) {
  // Only allow the specified fields and always set all required fields with defaults
  const filteredData = {
    uid,
    email: userData.email || '',
    avatar: userData.avatar || '',
    county: userData.county || '',
    city: userData.city || '',
    firstName: userData.firstName || '',
    lastName: userData.lastName || '',
    phone: userData.phone || '',
    postCode: userData.postCode || '',
    streetAddress: userData.streetAddress || '',
    termsAccepted: userData.termsAccepted === true, // only true if explicitly set
    totalAssessments: typeof userData.totalAssessments === 'number' ? userData.totalAssessments : 0,
    usedAssessments: typeof userData.usedAssessments === 'number' ? userData.usedAssessments : 0,
    assessments: Array.isArray(userData.assessments) ? userData.assessments : [],
    subscriptionName: userData.subscriptionName || '',
    subscriptionStatus: userData.subscriptionStatus || 'unpaid',
    subscriptionType: userData.subscriptionType || '',
    subscriptionStartAt: userData.subscriptionStartAt || null,
    subscriptionUpdateAt: userData.subscriptionUpdateAt || null,
    subscriptionEndedAt: userData.subscriptionEndedAt || null,
    isTrial: userData.isTrial === true, // only true if explicitly set
    trialStartDate: userData.trialStartDate || null,
    trialEndDate: userData.trialEndDate || null,
  };
  // Overwrite the document with only allowed fields
  await setDoc(doc(db, 'Users', uid), filteredData, { merge: false });
}

/**
 * Gets a user document from Firestore by UID.
 * @param {string} uid - The user UID
 * @returns {Promise<object|null>} The user data or null if not found
 */
export async function getUserByUid(uid) {
  const docSnap = await getDoc(doc(db, 'Users', uid));
  if (docSnap.exists()) {
    return docSnap.data();
  }
  return null;
}

/**
 * Updates a user's profile information in Firestore.
 * @param {string} uid - The user UID
 * @param {object} profileData - The profile data to update
 * @returns {Promise<void>}
 */
export async function updateUserProfile(uid, profileData) {
  const userRef = doc(db, 'Users', uid);
  await updateDoc(userRef, profileData);
} 